import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
	GateOffBehavior,
	LightBehavior,
	LightColor,
	LightingRingStyle,
	Response,
	DeviceTransmitterLightingBehavior
} from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { LightsApiService, LightsDevice } from '../api';

import { DeviceSysApiService } from './services';

@Injectable()
export class SysApiLightsApiService implements LightsApiService {
	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		private readonly deviceSysApiService: DeviceSysApiService
	) {
		this.logger = logger.createScopedLogger('SysApiLightsApiService');
	}

	public toggleAudioLightingActivityLeds(deviceId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleAudioLightingActivity()', 'toggling the audio level activity LEDs');
		return this.deviceSysApiService.toggleAudioLightingActivity(deviceId, enabled);
	}

	public setBrightness(deviceId: string, value: number): Observable<Response<void, string>> {
		this.logger.trace('setBrightness()', 'setting the brightness level');
		return this.deviceSysApiService.setBrightness(deviceId, value);
	}

	public clearLedErrors(deviceId: string): Observable<Response<void, string>> {
		this.logger.trace('clearLedErrors()', 'clearing error state on LEDs');
		return this.deviceSysApiService.clearLedErrors(deviceId);
	}

	public getDevice$(deviceId: string): Observable<LightsDevice> {
		this.logger.trace('getDevice$()', 'Getting lights-device', deviceId);
		return this.deviceSysApiService.getDevice$(deviceId);
	}

	public toggleNetworkJackLeds(deviceId: string, isOn: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleNetworkJackLeds()', 'toggling network jack LEDs', { deviceId, isOn });
		return this.deviceSysApiService.toggleNetworkJackLeds(deviceId, isOn);
	}

	public setLightRingStyle(deviceId: string, ringStyle: LightingRingStyle): Observable<Response<void, string>> {
		this.logger.trace('setLightRingStyle()', 'setting light ring style', { deviceId, ringStyle });
		return this.deviceSysApiService.setLightRingStyle(deviceId, ringStyle);
	}

	public toggleAutomixGating(deviceId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleAutomixGating()', 'toggling automix gating', { deviceId, enabled });
		return this.deviceSysApiService.toggleAutomixGating(deviceId, enabled);
	}

	public setGateOffBehavior(deviceId: string, behavior: GateOffBehavior): Observable<Response<void, string>> {
		this.logger.trace('setGateOffBehavior()', 'setting gate off behavior', { deviceId, behavior });
		return this.deviceSysApiService.setGateOffBehavior(deviceId, behavior);
	}

	public setUnmuteColor(deviceId: string, color: LightColor): Observable<Response<void, string>> {
		this.logger.trace('setUnmuteColor()', 'setting color for device is not muted.: ', { deviceId, color });
		return this.deviceSysApiService.setUnmuteColor(deviceId, color);
	}

	public setUnmuteBehavior(deviceId: string, behavior: LightBehavior): Observable<Response<void, string>> {
		this.logger.trace(
			'setUnmuteBehavior()',
			'setting the behavior of the devices LED, when the device is not muted',
			{ deviceId, behavior }
		);
		return this.deviceSysApiService.setUnmuteBehavior(deviceId, behavior);
	}

	public setMuteColor(deviceId: string, color: LightColor): Observable<Response<void, string>> {
		this.logger.trace('setMuteColor()', 'setting color for device is muted.: ', { deviceId, color });
		return this.deviceSysApiService.setMuteColor(deviceId, color);
	}

	public setMuteBehavior(deviceId: string, behavior: LightBehavior): Observable<Response<void, string>> {
		this.logger.trace('setMuteBehavior()', 'setting the behavior of the devices LED, when the device is muted', {
			deviceId,
			behavior
		});
		return this.deviceSysApiService.setMuteBehavior(deviceId, behavior);
	}

	public setTransmitterLightingBehavior(
		deviceId: string,
		behavior: DeviceTransmitterLightingBehavior
	): Observable<Response<void, string>> {
		this.logger.trace('setTransmitterLightingBehavior()', 'setting the behavior of the transmitter device LEDs', {
			deviceId,
			behavior
		});
		return this.deviceSysApiService.setTransmitterLightingBehavior(deviceId, behavior);
	}
}
